import { z } from 'zod';

/**
 * Chiffres uniquement, champ vide accepté : les deux champs téléphoniques
 * portent aujourd'hui un pattern="([0-9])*" et ne sont pas obligatoires, on
 * reprend donc exactement cette règle plutôt que de la durcir.
 */
const chiffresOuVide = z
  .string({ message: 'saisir_uniquement_des_chiffres' })
  .regex(/^[0-9]*$/, { message: 'saisir_uniquement_des_chiffres' })
  // nullish : les comptes existants peuvent avoir un téléphone à null, et
  // ce n'est pas une raison pour empêcher de les modifier.
  .nullish();

/** Une adresse e-mail seule, réutilisée pour chaque élément d'une liste. */
const adresseEmail = z.email();

/**
 * Schéma de la charge utile envoyée à la création d'un utilisateur.
 *
 * Attention à la correspondance avec les champs affichés : l'adresse e-mail est
 * saisie en deux morceaux, emailDebut et emailFin, que le formulaire réunit
 * autour d'un « @ ». La clé email du schéma couvre donc les deux champs, et le
 * message doit être rattaché aux deux à l'affichage.
 */
export const schemaNouvelUtilisateur = z.object({
  login_name: z.string({ message: 'valeur_invalide' }),

  lastname: z
    .string({ message: 'saisir_un_nom' })
    .trim()
    .min(1, { message: 'saisir_un_nom' }),

  name: z
    .string({ message: 'saisir_un_prenom' })
    .trim()
    .min(1, { message: 'saisir_un_prenom' }),

  email: z.email({ message: 'saisir_une_adresse_email_valide' }),

  phone_index: chiffresOuVide,
  phone_number: chiffresOuVide,

  // Facultatif aujourd'hui : aucun required sur le champ entreprise, et
  // null accepté pour la même raison que les champs téléphoniques.
  company_name: z.string({ message: 'valeur_invalide' }).nullish(),

  privileges_list: z.array(z.any(), { message: 'valeur_invalide' }),
  projects_list_id: z.array(z.any(), { message: 'valeur_invalide' }),
});

/**
 * Schéma de la charge utile envoyée à la modification d'un utilisateur.
 *
 * Même contrat, plus l'identifiant et l'état d'activation, que seul l'écran de
 * profil expose.
 *
 * La règle sur l'e-mail diffère de celle de la création. À la création,
 * l'adresse est construite par le formulaire à partir de deux champs réunis par
 * un « @ » : elle est unique par construction. En modification, le champ reçoit
 * ce que renvoie l'API, et les données existantes contiennent des utilisateurs
 * dont l'e-mail est une liste séparée par des virgules. Exiger une adresse
 * unique ici empêcherait purement et simplement de modifier ces utilisateurs :
 * on accepte donc une liste, mais chaque adresse doit être valide.
 */
export const schemaModificationUtilisateur = schemaNouvelUtilisateur.extend({
  user_id: z.coerce.number({ message: 'valeur_invalide' }).int(),

  // Une ou plusieurs adresses séparées par des virgules, chacune valide. Une
  // chaîne vide ou une virgule en trop laisse un morceau vide, qui est refusé.
  email: z
    .string({ message: 'saisir_une_adresse_email_valide' })
    .trim()
    .refine(
      (valeur) =>
        valeur
          .split(',')
          .every((adresse) => adresseEmail.safeParse(adresse.trim()).success),
      { message: 'saisir_une_adresse_email_valide' }
    ),

  // Tolérant : on ne bloque pas la modification d'un compte dont l'état
  // arriverait sous une forme inattendue, ce n'est pas le sujet de cet écran.
  active: z.coerce.number({ message: 'valeur_invalide' }).int(),
});
